import { type QueryKey, type QueryObserverOptions, useQuery } from '@tanstack/react-query';
import { receivableInvoicesApiNamespace } from 'receivable-invoices-js/constants/hosts';
import { camelizeKeys } from 'receivable-invoices-js/react/utils/camelize-keys';
import type { QuoteAPI } from 'receivable-invoices-js/react/api/models/quote';
import { qontoClient } from 'receivable-invoices-js/react/utils/api-clients/qonto';

interface Params {
  id: string;
}

interface QueryResponse {
  data: QuoteAPI;
}

interface CustomOptions {
  onError?: (error: unknown) => void;
}

const URL = `${receivableInvoicesApiNamespace}/receivable_invoices/quotes/shared`;

export function fetchQuoteKey(queryParams: Params): QueryKey {
  return ['quote', queryParams.id] as QueryKey;
}

export async function fetchQuote(params: Params, customOptions?: CustomOptions) {
  const { onError } = customOptions || {};

  try {
    const response = await qontoClient<QueryResponse>(`${URL}/${params.id}`);

    const { attributes, id, relationships } = response.data;
    const normalizedResponse = {
      ...attributes,
      id,
      // Extract only the IDs from relationships - we don't need the full relationship objects
      receivableInvoicingUploadId:
        relationships?.receivable_invoicing_upload?.data &&
        !Array.isArray(relationships.receivable_invoicing_upload.data)
          ? relationships.receivable_invoicing_upload.data.id
          : undefined,
      termsConditionsUploadId:
        relationships?.terms_conditions_upload?.data &&
        !Array.isArray(relationships.terms_conditions_upload.data)
          ? relationships.terms_conditions_upload.data.id
          : undefined,
    };

    return camelizeKeys(normalizedResponse);
  } catch (error) {
    if (onError) {
      onError(error);
    } else {
      throw error;
    }
  }
}

export type FetchQuoteResponse = Awaited<ReturnType<typeof fetchQuote>>;

export function useFetchQuote(
  params: Params,
  customOptions?: CustomOptions,
  queryOptions?: Partial<QueryObserverOptions<FetchQuoteResponse>>
) {
  return useQuery({
    queryKey: fetchQuoteKey(params),
    queryFn: () => fetchQuote(params, { ...customOptions }),
    ...queryOptions,
  });
}
